// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as api from '../../api/client'
import { startRecording, isRecording, subscribeRecording } from '../../support/replayRecorder'
import { friendlyError } from '../../api/errors'
import { ErrorBanner, LoadingState, EmptyState, relativeTime } from '../../components/workflow/StatePills'
import { SeverityBadge, StatusBadge } from './supportBadges'
import { usePaged, Pager, DEFAULT_PAGE_SIZE } from '../../components/Pager'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'

// The badge text as it reads on screen: on_hold -> "On hold", low -> "Low".
const badgeText = (s: string) => s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ') : s

// D-UI-2: what each column shows, for its header funnel.
const TICKET_FUNNELS: FunnelColumn<api.SupportTicketSummary>[] = [
  { key: 'no',       label: '#',             value: t => t.ticket_no },
  { key: 'subject',  label: 'Subject',       value: t => t.subject, kind: 'text' },
  { key: 'category', label: 'Category',      value: t => t.category },
  { key: 'severity', label: 'Severity',      value: t => badgeText(t.severity) },
  { key: 'status',   label: 'Status',        value: t => badgeText(t.status) },
  { key: 'activity', label: 'Last activity', value: t => t.last_activity_at },
]

const CATEGORIES: api.SupportCategory[] = ['billing', 'technical', 'clinical', 'account', 'other']
const SEVERITIES: api.SupportSeverity[] = ['low', 'normal', 'high', 'critical']

export default function SupportTicketsPage() {
  const navigate = useNavigate()
  const [items, setItems] = useState<api.SupportTicketSummary[]>([])
  // The header funnels filter the whole loaded set; paging happens after. KPI tiles stay on `items`.
  const fx = useTableFunnels(items, TICKET_FUNNELS)
  const { page, setPage, pageSize, setPageSize, pageRows, total, totalPages } = usePaged(fx.rows, DEFAULT_PAGE_SIZE, fx.sig)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [category, setCategory] = useState<api.SupportCategory>('technical')
  const [severity, setSeverity] = useState<api.SupportSeverity>('normal')
  const [submitting, setSubmitting] = useState(false)

  const load = () => {
    setLoading(true)
    api.listMySupportTickets()
      .then(r => setItems(r.items))
      .catch(e => setError(friendlyError(e)))
      .finally(() => setLoading(false))
  }
  useEffect(load, [])

  const [recording, setRecording] = useState(isRecording())
  useEffect(() => subscribeRecording(() => setRecording(isRecording())), [])

  // D-SUPPORT-1: "Record this issue" — starts a recording that PERSISTS across navigation.
  // A floating widget (globally mounted) shows the recording state and the Stop/attach control
  // on every screen, so the user can reproduce the bug wherever it happens and then send it.
  const beginRecording = async () => {
    if (recording) return
    const ok = window.confirm(
      'Record this issue?\n\nYour screen interactions across OwlRx will be recorded (typed text is masked) so support can see exactly what happened. A red “Recording” badge stays on screen — navigate to wherever the problem occurs, then press Stop to send it. Recording auto-stops after 15 minutes.')
    if (!ok) return
    await startRecording()
  }

  const submit = async () => {
    if (!subject.trim() || !body.trim()) return
    setSubmitting(true)
    try {
      const r = await api.createSupportTicket({ subject: subject.trim(), body: body.trim(), category, severity })
      setShowForm(false); setSubject(''); setBody(''); setCategory('technical'); setSeverity('normal')
      navigate(`/support/${r.id}`)
    } catch (e) {
      setError(friendlyError(e))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={{ padding: '24px 24px 48px', maxWidth: 'var(--page-max, 1100px)', margin: '0 auto', width: '100%' }}>
      <header style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Support</h1>
          <div style={{ color: 'var(--color-text-mute)', fontSize: 13, marginTop: 4 }}>Open a ticket and track our responses.</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={beginRecording} disabled={recording}
            style={{ ...primaryBtn, background: 'transparent', color: 'var(--color-text)', border: '1px solid var(--color-border)', opacity: recording ? 0.6 : 1 }}>
            {recording ? '● Recording… (use the badge to stop)' : '● Record this issue'}
          </button>
          {!showForm && <button onClick={() => setShowForm(true)} style={primaryBtn}>New ticket</button>}
        </div>
      </header>

      {error && <ErrorBanner message={error} onClose={() => setError(null)} />}

      {/* D-SUP-2 pharmacy dashboard: computed client-side from the already-loaded list — no extra API calls. */}
      {!loading && items.length > 0 && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
          {/* Phase A model + legacy rows: 'open' bucket absorbs legacy triage/in_progress. */}
          <MiniStat label="Open" value={items.filter(t => t.status === 'new' || t.status === 'open' || t.status === 'triage' || t.status === 'in_progress').length} />
          <MiniStat label="Waiting on you" value={items.filter(t => t.status === 'pending').length} />
          <MiniStat label="Solved" value={items.filter(t => t.status === 'solved' || t.status === 'resolved').length} />
          <MiniStat label="Closed" value={items.filter(t => t.status === 'closed').length} />
        </div>
      )}

      {showForm && (
        <div style={cardStyle}>
          <div style={{ fontWeight: 600, marginBottom: 12 }}>New support ticket</div>
          <label style={lbl}>Subject</label>
          <input value={subject} onChange={e => setSubject(e.target.value)} maxLength={200} style={input} placeholder="Brief summary of the issue" />
          {/* D-SUPPORT-2 §6 D3 (Sasha ruling 2026-08-16): subjects may be emailed to the support
              team, so steer users away from putting patient identifiers in them. The description
              stays inside the portal and is never emailed. */}
          <div style={{ fontSize: 11.5, color: 'var(--color-text-mute, #6b7280)', marginTop: 4 }}>
            Please don't include patient names or other identifying details in the subject — put those in the description below, which stays inside the portal.
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 160 }}>
              <label style={lbl}>Category</label>
              <select value={category} onChange={e => setCategory(e.target.value as api.SupportCategory)} style={input}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 160 }}>
              <label style={lbl}>Severity</label>
              <select value={severity} onChange={e => setSeverity(e.target.value as api.SupportSeverity)} style={input}>
                {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <label style={{ ...lbl, marginTop: 12 }}>Description</label>
          <textarea value={body} onChange={e => setBody(e.target.value)} style={{ ...input, minHeight: 120, resize: 'vertical' }}
            placeholder="What's happening? Include steps to reproduce if it's a bug. Avoid sharing more patient detail than necessary." />
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button onClick={submit} disabled={submitting || !subject.trim() || !body.trim()} style={primaryBtn}>
              {submitting ? 'Submitting…' : 'Submit ticket'}
            </button>
            <button onClick={() => setShowForm(false)} style={ghostBtn}>Cancel</button>
          </div>
        </div>
      )}

      <div style={cardStyle}>
        {loading ? <LoadingState label="Loading your tickets…" /> :
         items.length === 0 ? <EmptyState message="No support tickets yet. Open one when you need help." /> :
        <><Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} onPageSize={setPageSize} label="tickets" />
{fx.chips}
<table {...fx.tableProps} style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}># {fx.funnel('no')}</th>
              <th style={thStyle}>Subject {fx.funnel('subject')}</th>
              <th style={thStyle}>Category {fx.funnel('category')}</th>
              <th style={thStyle}>Severity {fx.funnel('severity')}</th>
              <th style={thStyle}>Status {fx.funnel('status')}</th>
              <th style={thStyle}>Last activity {fx.funnel('activity')}</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map(t => (
              <tr key={t.id} style={{ borderTop: '1px solid var(--color-border)', cursor: 'pointer' }}
                  onClick={() => navigate(`/support/${t.id}`)} tabIndex={0} role="button"
                  onKeyDown={e => { if (e.key === 'Enter') navigate(`/support/${t.id}`) }}>
                <td style={tdStyle}>{t.ticket_no}</td>
                <td style={{ ...tdStyle, fontWeight: 600 }}>{t.subject}</td>
                <td style={tdStyle}>{t.category}</td>
                <td style={tdStyle}><SeverityBadge severity={t.severity} /></td>
                <td style={tdStyle}><StatusBadge status={t.status} /></td>
                <td style={tdStyle}>{relativeTime(t.last_activity_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} showPageSize={false} label="tickets" /></>}
      </div>
    </div>
  )
}

const cardStyle: React.CSSProperties = { background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 12, padding: 16, marginBottom: 16 }
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }
const thStyle: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', color: 'var(--color-text-mute)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.4px' }
const tdStyle: React.CSSProperties = { padding: '10px 10px' }
const lbl: React.CSSProperties = { display: 'block', fontSize: 12.5, fontWeight: 600, color: 'var(--color-text-soft)', marginBottom: 4 }
const input: React.CSSProperties = { width: '100%', padding: '9px 11px', borderRadius: 8, border: '1px solid var(--color-border)', fontSize: 14, background: 'var(--color-bg)', color: 'var(--color-text)', boxSizing: 'border-box' }
const primaryBtn: React.CSSProperties = { padding: '9px 16px', borderRadius: 8, border: 'none', background: 'var(--color-primary,#2563eb)', color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer' }
const ghostBtn: React.CSSProperties = { padding: '9px 16px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'none', color: 'var(--color-text)', fontWeight: 600, fontSize: 14, cursor: 'pointer' }

function MiniStat({ label, value }: { label: string; value: number }) {
  return (
    <div style={{ flex: '1 1 120px', minWidth: 120, background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border)', borderRadius: 10, padding: '12px 16px' }}>
      <div style={{ fontSize: 12, color: 'var(--color-text-mute)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, marginTop: 2 }}>{value}</div>
    </div>
  )
}
